import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Switch,
  Tooltip,
} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import moment from 'moment';
import {
  GET_HELPDESK_CATEGORIES,
  GET_HELPDESK_FEATURES,
} from 'graphql/queries/flowBuilder';
import {TAB_ALLOWED_STATUSES} from '../helpdeskTicketsUtils';
import {keepOffered, topicOptions} from './topicFilter';
import filterStyles from './FilterSidebar.module.scss';

const {Option} = Select;

const STATUS_OPTIONS = [
  {value: 'GUIDED_FLOW', label: 'Guided Flow'},
  {value: 'AI_ACTIVE', label: 'AI Active'},
  {value: 'HUMAN_ACTIVE', label: 'Human Active'},
  {value: 'ESCALATED', label: 'Escalated'},
  {value: 'RESOLVED', label: 'Resolved'},
  {value: 'ABANDONED', label: 'Abandoned'},
];

/** Matches AdminTicketFilterInput (sidebar only; tab + search set elsewhere). */
const initialDraft = {
  categoryIds: [],
  featureIds: [],
  language: 'ALL',
  featureIssueId: undefined,
  statuses: [],
  unrated: false,
  staleAfter: null,
  aiTurnCountMin: undefined,
  csatScoreMin: undefined,
  csatScoreMax: undefined,
};

// CSAT score presets. Only meaningful on the resolved-tickets tab — that's
// the only place customers have closed-then-rated their own ticket.
const CSAT_PRESETS = [
  {value: 'ALL', label: 'All scores', min: undefined, max: undefined},
  {value: 'LOW', label: 'Low (1–2) — unhappy customers', min: 1, max: 2},
  {value: 'MID', label: 'Mid (3)', min: 3, max: 3},
  {value: 'HIGH', label: 'High (4–5) — testimonial candidates', min: 4, max: 5},
];

const csatPresetFromDraft = draft => {
  for (const preset of CSAT_PRESETS) {
    if (
      draft.csatScoreMin === preset.min &&
      draft.csatScoreMax === preset.max
    ) {
      return preset.value;
    }
  }
  return 'ALL';
};

export const FilterSidebar = ({
  draft,
  onDraftChange,
  onApply,
  onReset,
  activeTab,
}) => {
  const set = (key, value) => onDraftChange({...draft, [key]: value});

  // This queue is Mobile only, matching the ticket list's product.
  // Both languages are fetched once and narrowed client-side.
  const categoriesQuery = useQuery(GET_HELPDESK_CATEGORIES, {
    variables: {
      input: {product: 'MOBILE', languages: ['EN', 'FR'], isActive: true},
    },
    errorPolicy: 'all',
  });
  const categoriesNode = categoriesQuery.data?.getHelpdeskCategories;
  const categories =
    categoriesNode?.__typename === 'ResponseWithHelpdeskCategories'
      ? categoriesNode.data || []
      : null;

  const categoryIds = draft.categoryIds || [];
  const featuresQuery = useQuery(GET_HELPDESK_FEATURES, {
    variables: {
      input: {
        product: 'MOBILE',
        categoryIds,
        languages: ['EN', 'FR'],
        isActive: true,
      },
    },
    skip: !categoryIds.length,
    errorPolicy: 'all',
  });
  const featuresNode = featuresQuery.data?.getHelpdeskFeatures;
  const features =
    featuresNode?.__typename === 'ResponseWithHelpdeskFeatures'
      ? featuresNode.data || []
      : null;

  const topicChoices = categories
    ? topicOptions(categories, draft.language)
    : [];
  const featureChoices = features
    ? topicOptions(
        features.filter(f => categoryIds.includes(f.categoryId)),
        draft.language,
      )
    : [];
  const topicsError =
    categoriesNode?.__typename === 'Error'
      ? categoriesNode.message
      : categoriesQuery.error?.message;

  // A language switch hides the other language's topics: drop them from the
  // selection too, so the request never carries a hidden filter.
  const setLanguage = value => {
    const language = value || 'ALL';
    const nextCategories = keepOffered(
      categoryIds,
      categories ? topicOptions(categories, language) : [],
    );
    onDraftChange({
      ...draft,
      language,
      categoryIds: nextCategories,
      featureIds: nextCategories.length
        ? keepOffered(
            draft.featureIds,
            features
              ? topicOptions(
                  features.filter(f => nextCategories.includes(f.categoryId)),
                  language,
                )
              : [],
          )
        : [],
    });
  };

  // Features narrow within the chosen topics: unpicking a topic drops its
  // features.
  const setTopics = nextCategories =>
    onDraftChange({
      ...draft,
      categoryIds: nextCategories,
      featureIds: nextCategories.length
        ? keepOffered(
            draft.featureIds,
            features
              ? topicOptions(
                  features.filter(f => nextCategories.includes(f.categoryId)),
                  draft.language,
                )
              : [],
          )
        : [],
    });
  const staleMoment = draft.staleAfter ? moment(draft.staleAfter) : null;
  const popupContainer = trigger => trigger.parentNode;

  // Only offer statuses that belong to the current tab. `undefined`/null in the
  // map means "no constraint" → all statuses (e.g. My Tickets).
  const allowed = TAB_ALLOWED_STATUSES[activeTab];
  const statusOptions = allowed
    ? STATUS_OPTIONS.filter(o => allowed.includes(o.value))
    : STATUS_OPTIONS;
  // A single-status tab (Escalated/Resolved/Abandoned) doesn't need a picker.
  const showStatusFilter = statusOptions.length > 1;

  // CSAT and "unrated" filters only make sense when looking at resolved
  // tickets — that's the only state where the customer has had the chance
  // to rate (or skip rating) the ticket.
  const showCsatFilters = activeTab === 'resolved-tickets';

  const applyCsatPreset = presetValue => {
    const preset =
      CSAT_PRESETS.find(p => p.value === presetValue) || CSAT_PRESETS[0];
    onDraftChange({
      ...draft,
      csatScoreMin: preset.min,
      csatScoreMax: preset.max,
    });
  };

  return (
    <div className={filterStyles.dropdownContent}>
      <div className={filterStyles.header}>
        <h4>Filter Tickets</h4>
      </div>

      <div className={filterStyles.filterBody}>
        <div
          className={`${filterStyles.filterSection} ${filterStyles.filterSectionFull}`}>
          <label>
            {draft.language === 'FR' ? 'Sujet' : 'Topic'}{' '}
            <Tooltip
              title="The help-flow category the customer picked. Topics exist once per language: pick both the English and the French one to see every ticket on that subject. Tickets that skipped the help flow have no topic."
              placement="top"
              getPopupContainer={popupContainer}>
              <QuestionCircleOutlined style={{color: '#94a3b8'}} />
            </Tooltip>
          </label>
          <Select
            mode="multiple"
            placeholder="All topics"
            style={{width: '100%'}}
            allowClear
            loading={categoriesQuery.loading}
            value={categoryIds}
            onChange={setTopics}
            options={topicChoices}
            optionFilterProp="label"
            notFoundContent={
              topicsError ||
              (categoriesQuery.loading ? 'Loading topics…' : 'No topics')
            }
            getPopupContainer={popupContainer}
          />
        </div>

        <div className={filterStyles.filterSection}>
          <label>Language</label>
          <Select
            placeholder="All languages"
            style={{width: '100%'}}
            allowClear
            value={draft.language === 'ALL' ? undefined : draft.language}
            onChange={setLanguage}
            getPopupContainer={popupContainer}>
            <Option value="EN">English</Option>
            <Option value="FR">French</Option>
          </Select>
        </div>

        {categoryIds.length ? (
          <div className={filterStyles.filterSection}>
            <label>Feature</label>
            <Select
              mode="multiple"
              placeholder="All features in these topics"
              style={{width: '100%'}}
              allowClear
              loading={featuresQuery.loading}
              value={draft.featureIds || []}
              onChange={v => set('featureIds', v)}
              options={featureChoices}
              optionFilterProp="label"
              notFoundContent={
                featuresNode?.__typename === 'Error'
                  ? featuresNode.message
                  : featuresQuery.loading
                  ? 'Loading features…'
                  : 'No features'
              }
              getPopupContainer={popupContainer}
            />
          </div>
        ) : null}

        <div className={filterStyles.filterSection}>
          <label>Feature issue ID</label>
          <Input
            allowClear
            placeholder="Filter by issue ID…"
            value={draft.featureIssueId || ''}
            onChange={e => set('featureIssueId', e.target.value || undefined)}
          />
        </div>

        {showStatusFilter ? (
          <div className={filterStyles.filterSection}>
            <label>Status (multi-select)</label>
            <Select
              mode="multiple"
              placeholder="Any status"
              style={{width: '100%'}}
              allowClear
              value={draft.statuses || []}
              onChange={v => set('statuses', v)}
              options={statusOptions}
              optionFilterProp="label"
              getPopupContainer={popupContainer}
            />
          </div>
        ) : null}

        {showCsatFilters ? (
          <>
            <div className={filterStyles.filterSection}>
              <label>
                CSAT score{' '}
                <Tooltip
                  title="Customer Satisfaction Score (CSAT) — a 1-to-5 rating the customer leaves when they close their own ticket. Only resolved tickets have scores."
                  placement="top"
                  getPopupContainer={popupContainer}>
                  <QuestionCircleOutlined style={{color: '#94a3b8'}} />
                </Tooltip>
              </label>
              <Select
                style={{width: '100%'}}
                value={csatPresetFromDraft(draft)}
                onChange={applyCsatPreset}
                options={CSAT_PRESETS.map(p => ({
                  value: p.value,
                  label: p.label,
                }))}
                getPopupContainer={popupContainer}
              />
            </div>

            <div className={filterStyles.filterSection}>
              <label className={filterStyles.switchLabel}>
                <span>
                  Unrated only{' '}
                  <Tooltip
                    title="Show only resolved tickets where the customer never left a CSAT rating. Useful for chasing post-resolution feedback."
                    placement="top"
                    getPopupContainer={popupContainer}>
                    <QuestionCircleOutlined style={{color: '#94a3b8'}} />
                  </Tooltip>
                </span>
                <Switch
                  checked={!!draft.unrated}
                  onChange={v => set('unrated', v)}
                />
              </label>
            </div>
          </>
        ) : null}

        <div className={filterStyles.filterSection}>
          <label>Stale after</label>
          <DatePicker
            style={{width: '100%'}}
            showTime
            value={staleMoment}
            onChange={v => set('staleAfter', v ? v.toISOString() : null)}
            placeholder="Updated before…"
            getPopupContainer={popupContainer}
          />
        </div>

        <div className={filterStyles.filterSection}>
          <label>Min AI turns (loop probe)</label>
          <InputNumber
            style={{width: '100%'}}
            min={0}
            max={200}
            value={draft.aiTurnCountMin}
            onChange={v =>
              set('aiTurnCountMin', typeof v === 'number' ? v : undefined)
            }
            placeholder="e.g. 8"
          />
        </div>
      </div>

      <div className={filterStyles.actionBtns}>
        <Button style={{flex: 1}} onClick={onReset}>
          Reset
        </Button>
        <Button
          type="primary"
          onClick={onApply}
          style={{flex: 1, backgroundColor: '#3b82f6'}}>
          Apply filters
        </Button>
      </div>
    </div>
  );
};

export {initialDraft as filterSidebarInitialDraft};
